import { memo, useCallback, useEffect, useState } from "react";
import { Gift, CreditCard } from "lucide-react";
import type { BotezBokehTemplateProps } from "./types";
import { CORAL, CORAL_TEXT_LARGE, FONT_BODY, FONT_DISPLAY, FONT_KICKER, FONT_SCRIPT, MUTED_TEXT, TEAL, TEAL_DARK } from "./utils";
import { getEventGifts, type GiftCategory } from "@/services/api/gifts";
import { getPublicEventGifts, type PublicGiftItem } from "@/services/api/publicEvents";
import { PublicGiftContributionForm } from "@/components/public/PublicGiftContributionForm";
import { ContributionStatusCheck } from "@/components/public/ContributionStatusCheck";

/**
 * Gift registry wired exactly like modern-chic/Registry.tsx (public gifts
 * endpoint, JUB-53 card-payment gating, shared PublicGiftContributionForm),
 * restyled with the prototype's peachy/teal card look.
 */
export const BotezBokehRegistry = memo(({ event, config, locale, t }: BotezBokehTemplateProps) => {
  const [gifts, setGifts] = useState<PublicGiftItem[]>([]);
  const [categories, setCategories] = useState<GiftCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>("all");
  const [loading, setLoading] = useState(true);
  const [selectedGift, setSelectedGift] = useState<PublicGiftItem | null>(null);
  const [showContributionForm, setShowContributionForm] = useState(false);
  // JUB-53: card payments gate + connected account from the public payload.
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState<boolean | undefined>(undefined);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);

  const hasGiftRegistry = Boolean(
    event.registryPublic && event.giftsActive && (event as unknown as Record<string, boolean>).hasEnabledGifts,
  );

  const loadGifts = useCallback(
    (withFlags: boolean) => {
      if (!event.registryPublic || !event.giftsActive) return;

      // Public endpoint when we have a slug; authenticated fallback for draft previews.
      const fetchGifts = event.url
        ? getPublicEventGifts(event.url)
        : getEventGifts(event.uuid)
            .then((resp) => resp as unknown as { gifts: PublicGiftItem[]; categories: GiftCategory[] })
            .catch(() => {
              if (event.uuid) {
                return getPublicEventGifts(event.uuid);
              }
              throw new Error("No valid event identifier");
            });

      fetchGifts
        .then((response) => {
          const resp = response as unknown as Record<string, unknown>;
          const respData = (resp.data || {}) as Record<string, unknown>;
          const giftsList = Array.isArray(resp.gifts)
            ? (resp.gifts as PublicGiftItem[])
            : Array.isArray(respData.gifts)
              ? (respData.gifts as PublicGiftItem[])
              : [];
          const categoriesList = Array.isArray(resp.categories)
            ? (resp.categories as GiftCategory[])
            : Array.isArray(respData.categories)
              ? (respData.categories as GiftCategory[])
              : [];
          setGifts(giftsList.filter((g) => g && (g.enabled === true || g.enabled === undefined)));
          setCategories(categoriesList);
          if (withFlags) {
            // JUB-53 flags (public endpoint only; undefined on the authenticated fallback)
            setCardPaymentsEnabled((resp as { cardPaymentsEnabled?: boolean }).cardPaymentsEnabled);
            setStripeAccountId((resp as { stripeAccountId?: string | null }).stripeAccountId ?? null);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching gifts:", error);
          setGifts([]);
          setCategories([]);
          setLoading(false);
        });
    },
    [event.giftsActive, event.registryPublic, event.uuid, event.url],
  );

  useEffect(() => {
    if (!event.registryPublic || !event.giftsActive) {
      setGifts([]);
      setCategories([]);
      setLoading(false);
      return;
    }
    loadGifts(true);
  }, [event.giftsActive, event.registryPublic, loadGifts]);

  const filteredGifts =
    activeCategory === "all"
      ? gifts
      : gifts.filter((gift) => gift.category?.id === activeCategory || gift.category?.name === activeCategory);

  const allCategories = [{ id: "all", name: t("events.form.template.allItems") }, ...categories];

  if (!hasGiftRegistry) {
    return null;
  }

  return (
    <section id="gifts" className="relative z-10 py-16" style={{ background: config.palette.surface }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p
            className="text-xs tracking-[0.3em] uppercase mb-4 font-medium"
            style={{ fontFamily: FONT_KICKER, color: TEAL }}
          >
            {t("events.form.template.giftRegistry")}
          </p>
          <h2
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
          >
            {t("events.form.template.celebratingWithLove")}
          </h2>
          <p
            className="text-base max-w-2xl mx-auto break-words"
            style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
          >
            {t("events.form.template.giftRegistryDescription")}
          </p>
        </div>

        {/* Category tabs */}
        {allCategories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {allCategories.map((cat) => {
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id ?? cat.name}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className="px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-medium border-2 transition-all duration-300"
                  style={{
                    fontFamily: FONT_KICKER,
                    backgroundColor: active ? TEAL : "transparent",
                    color: active ? "#ffffff" : TEAL,
                    borderColor: active ? TEAL : `${TEAL}4D`,
                  }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Gift grid */}
        {loading ? (
          <p
            className="text-center text-sm"
            style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
          >
            {t("common.loading")}
          </p>
        ) : filteredGifts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredGifts.map((gift) => (
              <div
                key={gift.id}
                className="rounded-2xl overflow-hidden border-2 transition-all duration-300 flex flex-col"
                style={{
                  background: config.palette.background,
                  borderColor: `${TEAL}33`,
                }}
              >
                <div className="relative aspect-square overflow-hidden">
                  {gift.cover ? (
                    <img
                      src={gift.cover}
                      alt={gift.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: config.palette.surfaceAlt }}
                    >
                      <Gift className="h-12 w-12 opacity-30" style={{ color: TEAL }} aria-hidden="true" />
                    </div>
                  )}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  {gift.category && (
                    <p
                      className="text-xs tracking-[0.2em] uppercase mb-2 font-medium"
                      style={{ fontFamily: FONT_KICKER, color: TEAL_DARK }}
                    >
                      {gift.category.name}
                    </p>
                  )}
                  <h3
                    className="text-2xl mb-2 font-bold break-words"
                    style={{ fontFamily: FONT_DISPLAY, color: config.palette.text }}
                  >
                    {gift.name}
                  </h3>
                  {gift.description && (
                    <p
                      className="text-sm mb-4 break-words"
                      style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
                    >
                      {gift.description}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedGift(gift);
                      setShowContributionForm(true);
                    }}
                    className="mt-auto w-full py-3 rounded-lg text-white text-sm uppercase tracking-wider transition-all duration-300 motion-safe:hover:scale-[1.02] shadow-lg font-medium"
                    style={{ fontFamily: FONT_KICKER, backgroundColor: TEAL }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
                  >
                    {t("events.form.template.contributeGift")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Universal gift note */}
        <div
          className="max-w-xl mx-auto text-center p-8 rounded-3xl border-2"
          style={{ background: config.palette.background, borderColor: `${CORAL}33` }}
        >
          <p
            className="text-xs tracking-[0.2em] uppercase mb-4 font-medium"
            style={{ fontFamily: FONT_KICKER, color: TEAL_DARK }}
          >
            {t("events.form.template.universalGiftNote")}
          </p>
          <p
            className="text-sm mb-6 break-words"
            style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
          >
            {t("events.form.template.universalGiftNoteDescription")}
          </p>
          <div className="flex justify-center opacity-50">
            <CreditCard className="h-8 w-8" style={{ color: TEAL }} aria-hidden="true" />
          </div>
        </div>

        {/* JUB-98: guest-visible entry point for the JUB-79 contribution status check */}
        <div className="text-center">
          <ContributionStatusCheck
            event={event}
            triggerClassName="text-sm font-medium underline underline-offset-4 hover:no-underline transition-colors"
            triggerStyle={{ color: MUTED_TEXT, fontFamily: FONT_BODY }}
          />
        </div>
      </div>

      {/* Shared gift contribution dialog (replaces the prototype's ContributionModal) */}
      {selectedGift && (
        <PublicGiftContributionForm
          event={event}
          gift={selectedGift}
          config={config}
          locale={locale}
          cardPaymentsEnabled={cardPaymentsEnabled}
          stripeAccountId={stripeAccountId}
          open={showContributionForm}
          onOpenChange={(open) => {
            setShowContributionForm(open);
            if (!open) {
              setSelectedGift(null);
            }
          }}
          onSuccess={() => loadGifts(false)}
        />
      )}
    </section>
  );
});

BotezBokehRegistry.displayName = "BotezBokehRegistry";
